import { apiCall } from "./base";

/**
 * Multiplay room membership HTTP API.
 * JSON field names use snake_case to match backend Pydantic models and
 * ViewRCloudApiClient.cs (Unity cloud client).
 */

export interface MultiplayRoomPrepareBody {
  display_name?: string;
}

export interface MultiplayRoomRegisterBody {
  room_name?: string;
  gcs_path: string;
  share_code?: string;
  display_name?: string;
}

export interface MultiplayRoomMemberBody {
  room_name?: string;
  share_code?: string;
  display_name?: string;
}

export interface MultiplayRoomDownloadUrlBody {
  room_name: string;
  gcs_path: string;
}

export interface MultiplayRoomMemberEntry {
  uid: string;
  display_name?: string;
}

export interface MultiplayRoomMembershipResponse {
  ok: boolean;
  room_name: string;
  share_code: string;
  host_uid: string;
  gcs_path: string;
  member_count: number;
  max_members: number;
  members?: MultiplayRoomMemberEntry[];
}

export interface MultiplayRoomDownloadUrlResponse {
  ok: boolean;
  room_name: string;
  host_uid: string;
  gcs_path: string;
  resolved_gcs_path?: string;
  bucket?: string;
  input_bucket?: string;
  service_account?: string;
  signed_for_uid?: string;
  download_url: string;
  expires_in_hours: number;
}

/**
 * Fusion relay envelope — matches packages/ViewREngine/.../MultiplayCommandEnvelope.cs.
 * Transport is Fusion RpcRelayCommand, NOT backend HTTP.
 */
export type MultiplayCommandType =
  | "Unknown"
  | "LoadScene"
  | "StartHmi"
  | "ShowToast"
  | "ApplyViewRDataSnapshot"
  | "DebugPing"
  | "SyncSpaceVse"
  | "SyncPresenterViewRDataBlob"
  | "PresenterUiPatch"
  | "SyncVseWorldState"
  | "HostSessionEnding";

export interface MultiplayCommandEnvelope {
  commandId?: string;
  revision: number;
  commandType: MultiplayCommandType;
  payloadJson?: string;
  applyToHost?: boolean;
}

export const prepareMultiplayRoom = async (
  body: MultiplayRoomPrepareBody = {}
): Promise<MultiplayRoomMembershipResponse> => {
  return apiCall("/api/multiplay/rooms/prepare", {
    method: "POST",
    body: JSON.stringify(body),
  }) as Promise<MultiplayRoomMembershipResponse>;
};

export const registerMultiplayRoom = async (
  body: MultiplayRoomRegisterBody
): Promise<MultiplayRoomMembershipResponse> => {
  return apiCall("/api/multiplay/rooms/register", {
    method: "POST",
    body: JSON.stringify(body),
  }) as Promise<MultiplayRoomMembershipResponse>;
};

export const joinMultiplayRoom = async (
  body: MultiplayRoomMemberBody
): Promise<MultiplayRoomMembershipResponse> => {
  return apiCall("/api/multiplay/rooms/join", {
    method: "POST",
    body: JSON.stringify(body),
  }) as Promise<MultiplayRoomMembershipResponse>;
};

export const leaveMultiplayRoom = async (
  body: MultiplayRoomMemberBody
): Promise<MultiplayRoomMembershipResponse> => {
  return apiCall("/api/multiplay/rooms/leave", {
    method: "POST",
    body: JSON.stringify(body),
  }) as Promise<MultiplayRoomMembershipResponse>;
};

export const getMultiplayRoomByShareCode = async (
  shareCode: string
): Promise<MultiplayRoomMembershipResponse> => {
  const encoded = encodeURIComponent(shareCode);
  return apiCall(`/api/multiplay/rooms/by-code/${encoded}`, {
    method: "GET",
  }) as Promise<MultiplayRoomMembershipResponse>;
};

export const requestMultiplayRoomDownloadUrl = async (
  body: MultiplayRoomDownloadUrlBody
): Promise<MultiplayRoomDownloadUrlResponse> => {
  return apiCall("/api/multiplay/rooms/download-url", {
    method: "POST",
    body: JSON.stringify(body),
  }) as Promise<MultiplayRoomDownloadUrlResponse>;
};

export const getMultiplaySessionStatus = async (
  roomName: string
): Promise<MultiplayRoomMembershipResponse> => {
  const encodedRoom = encodeURIComponent(roomName);
  return apiCall(`/api/multiplay/session/${encodedRoom}`, {
    method: "GET",
  }) as Promise<MultiplayRoomMembershipResponse>;
};

/**
 * Relay commands are sent over Fusion (ViewRMultiplayAvatar.RpcRelayCommand), not Main API.
 * This helper exists for contract parity and fails fast if called from React.
 */
export const relayMultiplayCommand = async (
  _envelope: MultiplayCommandEnvelope
): Promise<never> => {
  throw new Error(
    "Multiplay relay uses Fusion RPC (MultiplayCommandEnvelope), not /api/multiplay/relay-command."
  );
};
